import { Helpers } from '@utils/helper'
import { LodashUtils } from '@utils/lodash'
import dayjs from 'dayjs'
import { FILE_FORMAT, MEASUREMENT_UNIT, TIME_FRAME } from './common.model'

export namespace WaterAccountManagement {
  export enum ACCOUNT_ROLE {
    OWNER = 1,
    REQUESTOR,
    HOUSEHOLD_MEMBER,
  }

  export enum ACCOUNT_STATUS {
    PENDING_AUTHORIZATION = 1,
    APPROVED,
    REQUEST_SENT,
  }

  export enum ACCOUNT_PERMISSION {
    FULL_ACCESS = 1,
    LIMITED_ACCESS,
  }

  export interface IConsumptionData {
    consumption: number
    endDate: string //'MM/DD/YYYY';
    endDateHour: number
    endDateTime: string // '2020-11-30T00:00:00' ;
    startDate: string //'MM/DD/YYYY';
    startDateHour: number
    startDateTime: string // '2020-11-01T00:00:00';
    estateAverageConsumption: number
    nationalAverageConsumption: number
  }

  export interface ISelectedAccount {
    isMerged: boolean
    mergedId: string
    contractNumber: string
  }

  export interface IMeterData {
    consumptionLastDate: string
    contractAccountNumber: string
    lastUpdated: string
    estateAverageConsumption: number
    nationalAverageConsumption: number
    estateUpdatedDate: string
    nationalUpdatedDate: string
    data: IConsumptionData[]
  }

  export interface IMembersAccessListPayload {
    contractAccountNumber: string
    page?: number // start from 1
    pageSize?: number
  }

  export interface IDownloadUsageData {
    timeFrame: TIME_FRAME
    fromDate: Date
    toDate: Date
    exportFormat: FILE_FORMAT
    unit: MEASUREMENT_UNIT
  }

  export class WaterAccountModel {
    public accountNumber: string
    public accountName: string
    public ownersName: string
    public sortOrder: number
    public displayedAddress: string[]
    public isBusinessAccount?: boolean
    public isPending?: boolean
    public role: ACCOUNT_ROLE
    public requestId?: string
    public requestedAccessStatus?: ACCOUNT_STATUS
    public occupancy?: number
    public typeOfWater?: string
    public userPermission?: ACCOUNT_PERMISSION
    public mergedWaterAccountId: string
    public mergedWaterAccounts: any[]
    public uen: string
    public connectionObject: string

    constructor(initial: Partial<WaterAccountModel> | null) {
      this.mergedWaterAccountId = LodashUtils.get(
        initial,
        'mergedWaterAccountId',
        '',
      )
      this.mergedWaterAccounts = LodashUtils.get(
        initial,
        'mergedWaterAccounts',
        [],
      )
      this.uen = LodashUtils.get(initial, 'uen', '')
      this.connectionObject = LodashUtils.get(initial, 'connectionObject', '')
      this.accountName = LodashUtils.get(initial, 'accountName', '')
      this.ownersName = LodashUtils.get(initial, 'ownersName', '')
      this.accountNumber = LodashUtils.get(initial, 'accountNumber', '')
      this.sortOrder = LodashUtils.get(initial, 'sortOrder', -1)
      this.displayedAddress = LodashUtils.get(initial, 'displayedAddress', [])
      this.isBusinessAccount = LodashUtils.get(
        initial,
        'isBusinessAccount',
        false,
      )
      this.isPending = LodashUtils.get(initial, 'isPending', false)
      this.role = LodashUtils.get(initial, 'role', ACCOUNT_ROLE.OWNER)
      this.requestId = LodashUtils.get(initial, 'requestId', undefined)
      this.requestedAccessStatus = LodashUtils.get(
        initial,
        'requestedAccessStatus',
        undefined,
      )
      this.occupancy = LodashUtils.get(initial, 'occupancy', undefined)
      this.typeOfWater = LodashUtils.get(initial, 'typeOfWater', '')
      this.userPermission = LodashUtils.get(
        initial,
        'userPermission',
        undefined,
      )
    }
  }

  export class WaterAccountDetailModel {
    public contractAccountNumber: string
    public accountName: string
    public monthlyGoal: number
    public status: ACCOUNT_STATUS
    public accountType: string
    public housingType: string
    public blockNumber: string
    public streetName: string
    public floorUnitNumber: string
    public postalCode: string
    public typeOfWater?: string
    public sector?: string
    public isBusinessAccount: boolean
    public suggestedUsage: {
      premiseType: string
      cuM: number
      noOfHouseHold: number
    } | null
    public role: ACCOUNT_ROLE
    public mergedWaterAccountId: string
    public mergedWaterAccounts: any[]

    constructor(initial?: Partial<WaterAccountDetailModel>) {
      this.mergedWaterAccountId = LodashUtils.get(
        initial,
        'mergedWaterAccountId',
        '',
      )
      this.mergedWaterAccounts = LodashUtils.get(
        initial,
        'mergedWaterAccounts',
        [],
      )
      this.contractAccountNumber = LodashUtils.get(
        initial,
        'contractAccountNumber',
        '',
      )
      this.accountName = LodashUtils.get(initial, 'accountName', '')
      this.monthlyGoal = LodashUtils.get(initial, 'monthlyGoal', 0)
      this.status = LodashUtils.get(initial, 'status', ACCOUNT_STATUS.APPROVED)
      this.accountType = LodashUtils.get(initial, 'accountType', '')
      this.housingType = LodashUtils.get(initial, 'housingType', '')
      this.blockNumber = LodashUtils.get(initial, 'blockNumber', '')
      this.streetName = LodashUtils.get(initial, 'streetName', '')
      this.floorUnitNumber = LodashUtils.get(initial, 'floorUnitNumber', '')
      this.postalCode = LodashUtils.get(initial, 'postalCode', '')
      this.typeOfWater = LodashUtils.get(initial, 'typeOfWater', '')
      this.sector = LodashUtils.get(initial, 'sector', '')
      this.isBusinessAccount = LodashUtils.get(
        initial,
        'isBusinessAccount',
        false,
      )
      this.suggestedUsage = LodashUtils.get(initial, 'suggestedUsage', null)
      this.role = LodashUtils.get(initial, 'role', ACCOUNT_ROLE.OWNER)
    }
  }

  export class MemberAccess {
    requestId: string
    contractAccountNumber: string
    requestorId: string
    registeredName: string
    requestorName: string
    requestorEmail: string
    requestorContactNumber: string
    requestorNRIC: string
    userPermission: ACCOUNT_PERMISSION
    requestedDate: string
    startDate: Date | null
    endDate: Date | null
    noDurationLimit: boolean
    status: ACCOUNT_STATUS | null
    userRole: ACCOUNT_ROLE

    constructor(initial: Partial<MemberAccess> | null) {
      this.requestId = LodashUtils.get(initial, 'requestId', '')
      this.contractAccountNumber = LodashUtils.get(
        initial,
        'contractAccountNumber',
        '',
      )
      this.requestorId = LodashUtils.get(initial, 'requestorId', '')
      this.registeredName = LodashUtils.get(initial, 'registeredName', '')
      this.requestorName = LodashUtils.get(initial, 'requestorName', '')
      this.requestorEmail = LodashUtils.get(initial, 'requestorEmail', '')
      this.requestorContactNumber = LodashUtils.get(
        initial,
        'requestorContactNumber',
        '',
      )
      this.requestorNRIC = LodashUtils.get(initial, 'requestorNRIC', '')
      this.userPermission = Helpers.toNumber(
        LodashUtils.get(
          initial,
          'userPermission',
          ACCOUNT_PERMISSION.LIMITED_ACCESS,
        ),
      )
      this.requestedDate = LodashUtils.get(initial, 'requestedDate', '')
      this.userRole = Helpers.toNumber(
        LodashUtils.get(initial, 'userRole', ACCOUNT_ROLE.REQUESTOR),
      )

      const startDate = LodashUtils.get(initial, 'startDate', null)
      const endDate = LodashUtils.get(initial, 'endDate', null)
      this.startDate = startDate ? new Date(startDate) : null
      this.endDate = endDate ? new Date(endDate) : null
      this.status = LodashUtils.get(initial, 'status', null)
      this.noDurationLimit =
        initial === null ? true : Boolean(!this.startDate && !this.endDate)
    }
  }

  export class RegisterAccount {
    accountNumber: string
    displayedAddress: string
    email: string | null
    phoneNumber: string | null
    isGranted: boolean
    isOwner: boolean
    postalCode: string

    constructor(initial: RegisterAccount | null) {
      this.accountNumber = LodashUtils.get(initial, 'accountNumber', '')
      this.displayedAddress = LodashUtils.get(initial, 'displayedAddress', '')
      this.email = LodashUtils.get(initial, 'email', null)
      this.phoneNumber = LodashUtils.get(initial, 'phoneNumber', null)
      this.isGranted = LodashUtils.get(initial, 'isGranted', false)
      this.isOwner = LodashUtils.get(initial, 'isOwner', false)
      this.postalCode = LodashUtils.get(initial, 'postalCode', '')
    }
  }
  export const HOUSEHOLD_MEMBER_OPTION_ID =
    '46c4b2db-71df-4662-b9a3-ac1c17ce5dd8'
  export interface ITypeOption {
    id: string
    label: string
  }

  export class HouseHoldProfile {
    id: string
    label: string
    selectedOption: string | null | undefined
    typeOptions: ITypeOption[]

    constructor(initial: HouseHoldProfile | null) {
      this.id = LodashUtils.get(initial, 'id', '')
      this.label = LodashUtils.get(initial, 'label', '')
      this.selectedOption = LodashUtils.get(initial, 'selectedOption', null)
      this.typeOptions = LodashUtils.get(initial, 'typeOptions', [])
    }
  }

  /**
   * X: This month's consumption till current date; (total value of current month);
   * Y: Personal Target
   * N: Current day count (within month);
   * D: Days in Month
   * All days: X >= Y --> Red
   * First 14 days: X < Y --> Green
   * 15 days onward: X < (Y * N) / D --> Green
   * 15th day onward: (Y * N) / D <= X < Y Orange
   */

  type Status = 'green' | 'orange' | 'red' | 'normal'

  export class MonthlyWaterUsageStatus {
    public x: number
    public y?: number
    readonly n: number
    readonly d: number
    readonly initialStatus: Status
    constructor({
      thisMonthConsumption,
      waterMonthlyGoal,
      currentDate,
    }: {
      thisMonthConsumption: number
      waterMonthlyGoal: number
      currentDate: number
    }) {
      this.x = thisMonthConsumption
      this.y = waterMonthlyGoal
      const today = dayjs()
      this.n = currentDate
      this.d = today.daysInMonth()
      this.initialStatus = this.calculate()
    }

    public get remainingDate(): number {
      return this.d - this.n
    }

    private calculate(): Status {
      if (!this.y || this.y < 0) {
        return 'normal'
      }
      if (this.x >= this.y) {
        return 'red'
      }
      if (this.n > 14) {
        const e = (this.y * this.n) / this.d
        if (this.x < e) {
          return 'green'
        }
        if (e <= this.x && this.x < this.y) {
          return 'orange'
        }
      }
      return 'green'
    }

    get deviantValue(): number {
      if (!this.y || this.y < 0) {
        return 0
      }
      return Math.abs(this.y - this.x)
    }

    get usageValue(): number | null {
      return this.x >= 0 ? this.x : null
    }

    get status(): Status {
      return this.initialStatus
    }
  }

  export interface IAddNewAccountForm {
    accountNumber: string
    postalCode: string
  }
}
